// Birthdays: this month's for clients and staff, and today's.
//
// Active clients (never closed ones) and active staff, employees, contractors
// and agency staff alike. A client's age is shown (it helps the care team); a
// staff member's never is. Dates of birth are calendar dates, so everything
// here works on "YYYY-MM-DD" strings and local calendar parts — never a
// Date parsed from the string, which would shift a day west of UTC.
import { supabase } from './supabase';
import { isClosedClient } from './serviceModel';

const pad = (n) => String(n).padStart(2, '0');
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/** Month and day this person celebrates in `year`. 29 Feb falls on 28 Feb in other years. */
export const birthdayIn = (dob, year) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob || '');
  if (!m) return null;
  let month = Number(m[2]);
  let day = Number(m[3]);
  if (month === 2 && day === 29 && !isLeap(year)) day = 28;
  return { month, day, birthYear: Number(m[1]) };
};

export const EMPLOYMENT_LABEL = { employee: 'Staff', contractor: 'Contractor', agency: 'Agency' };

/**
 * People whose birthday falls in `today`'s month, in date order.
 * Each: { key, kind: 'client'|'staff', id, name, day, isToday, isPast, turns, role, type }.
 */
export const birthdaysThisMonth = ({ clients = [], staff = [] }, today = new Date()) => {
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const todayDay = today.getDate();
  const out = [];
  for (const c of clients) {
    if (isClosedClient(c)) continue;
    const b = birthdayIn(c.date_of_birth, year);
    if (!b || b.month !== month) continue;
    out.push({
      key: `c${c.id}`, kind: 'client', id: c.id, ref: c.client_id,
      name: `${c.last_name}, ${c.first_name}`, day: b.day,
      isToday: b.day === todayDay, isPast: b.day < todayDay,
      turns: year - b.birthYear, type: 'Client',
    });
  }
  for (const s of staff) {
    if (s.status !== 'active') continue;
    const b = birthdayIn(s.date_of_birth, year);
    if (!b || b.month !== month) continue;
    out.push({
      key: `s${s.id}`, kind: 'staff', id: s.id, profileId: s.profile_id || null,
      name: s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name,
      firstName: s.goes_by || s.first_name || (s.full_name || '').split(' ')[0],
      day: b.day, isToday: b.day === todayDay, isPast: b.day < todayDay,
      role: s.position_code || null, type: EMPLOYMENT_LABEL[s.employment_type] || 'Staff',
    });
  }
  return out.sort((a, b) => a.day - b.day || a.name.localeCompare(b.name));
};

/** One line under a name: "turns 4" for a client, role and type for staff. */
export const birthdaySub = (p) => (p.kind === 'client'
  ? `turns ${p.turns}`
  : [p.role, p.type].filter(Boolean).join(' · '));

/** Loads what the banner and pop-up need, plus how many active staff have no date of birth. */
export const loadBirthdays = async (today = new Date()) => {
  const [cRes, sRes] = await Promise.all([
    supabase.from('clients').select('id, client_id, first_name, last_name, date_of_birth, current_status').not('date_of_birth', 'is', null),
    supabase.from('staff').select('id, profile_id, first_name, last_name, full_name, goes_by, date_of_birth, position_code, employment_type, status').eq('status', 'active'),
  ]);
  if (cRes.error || sRes.error) return { error: cRes.error || sRes.error, people: [], staffMissingDob: 0 };
  return {
    error: null,
    people: birthdaysThisMonth({ clients: cRes.data || [], staff: sRes.data || [] }, today),
    staffMissingDob: (sRes.data || []).filter(s => !s.date_of_birth).length,
  };
};

// ── Once per person per day ────────────────────────────────────────────────
// Kept in this browser: signing in on a second device the same day shows it
// once more there, which is harmless.
const seenKey = (userId) => `caera.birthdays.seen.${userId}`;
export const popupSeenToday = (userId, today = new Date()) => {
  try { return localStorage.getItem(seenKey(userId)) === ymd(today); } catch { return false; }
};
export const markPopupSeen = (userId, today = new Date()) => {
  try { localStorage.setItem(seenKey(userId), ymd(today)); } catch { /* storage blocked: it shows again */ }
};
